import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  viewChild,
} from '@angular/core';
import { IconsInitConfigType } from '../../../../constants';
import { IconUIComponent } from '../../../atoms/icon/icon.component';
import {
  SMART_FILTER_CHIP_COLORS_MAP,
  SMART_FILTER_CHIP_SIZES_MAP,
} from '../../../../constants/smart-filter-chip.constants';
import { SmartFilterChipColorType, SmartFilterChipSizeType } from '../../../../types';

const OUTLINED =
  'tw:border-gray-4 tw:bg-transparent tw:text-gray-1 tw:hover:border-gray-2 tw:hover:text-white';
const FILLED = 'tw:border-transparent tw:text-black';

@Component({
  selector: 'fr8bot-ui-smart-filter-chip',
  imports: [IconUIComponent],
  templateUrl: './smart-filter-chip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartFilterChipUIComponent {
  readonly iconName = input<IconsInitConfigType>('checkmark-2-16');
  readonly color = input<SmartFilterChipColorType>('transparent');
  readonly size = input<SmartFilterChipSizeType>('xs');
  readonly selected = input<boolean>(false);

  private readonly box = viewChild.required<ElementRef<HTMLElement>>('box');
  private readonly icon = viewChild.required('icon', { read: ElementRef<HTMLElement> });
  private readonly label = viewChild.required<ElementRef<HTMLElement>>('label');

  // Outlined and filled are exclusive: Tailwind here is `!important`, so both sets of colors at
  // once would leave the winner to stylesheet order.
  protected readonly classes = computed(() => {
    const filled = this.selected() && this.color() !== SmartFilterChipColorType.Transparent;
    const look = filled ? `${FILLED} ${SMART_FILTER_CHIP_COLORS_MAP[this.color()]}` : OUTLINED;

    return `${SMART_FILTER_CHIP_SIZES_MAP[this.size()]} ${look}`;
  });

  /**
   * The width the chip needs to show its label on one line. A layout read: call it only from an
   * observer or event callback.
   */
  naturalWidth(): number {
    const box = this.box().nativeElement;
    const style = getComputedStyle(box);
    const px = (value: string) => parseFloat(value) || 0;

    return (
      this.label().nativeElement.scrollWidth +
      this.icon().nativeElement.offsetWidth +
      px(style.columnGap) +
      px(style.paddingLeft) +
      px(style.paddingRight) +
      px(style.borderLeftWidth) +
      px(style.borderRightWidth)
    );
  }
}
